import { Alert, Button, Table } from 'antd';
import moment from 'moment';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import OWStaticSelect from '../ow_static_select/OWStaticSelect';
import OWDateRangePicker from '../ow_date_range_picker/OWDateRangePicker';
import { FILTER_FIELD_TYPE } from '../../utils/DataConstants';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import queryString from 'qs';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import useMountEffect from '../../hooks/useMountEffect';
import { useFillViewportHeight } from './useFillViewportHeight';
import AOPEventSentence from './AOPEventSentence';
import { withUserType } from '../withUserType';
import { EmptyState } from '../empty_state/EmptyState';
import DateTimeHover from '../date_time_component/DateTime';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	AOPEventOrder,
	AOPExecution,
	AOPProcedureEvent,
	pageRows,
} from '../../types/AOPExecutionTypes';
import {
	isCancelledRead,
	readExecution,
	readProcedureEvents,
	readProcedureExecutions,
} from '../../thunks/agent_executions_thunks';
import { workOrdersRestCrudThunksForBuyer } from '../../thunks/work_orders_thunks';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';
import {
	AOPRunState as AOPRunStateName,
	AOP_RUN_ORDER,
	AOP_RUN_STATES,
	filtersForRunState,
	runStateOf,
} from './utils/AOPExecutionStates';
import OscarMark from '../../ai_agent/components/shared/OscarMark';
import { describeEvent, isListedEvent, positionOf } from './utils/AOPExecutionUtils';
import { fetchProcedureVersion } from '../../thunks/agent_procedures_thunks';
import { versionKey } from '../../reducers/agent_procedure_versions_reducer';

/*
  This tab's own, not the Work Orders tab's.

  Both read work orders for a procedure and both used `aopExecutionWorkOrders`,
  which is one collection with two writers: switching tabs while a search was in
  flight let a late answer replace the table's `recordsIds` and pagination with
  this feed's ids. Nothing here reads the collection — the rows take their
  titles from the merged base records by id — so it only ever had to be a name
  nobody else owns.
*/
const WORK_ORDER_TC = 'aopActivityWorkOrders';
const PAGE_SIZE = 50;

/**
 * Everything the feed is narrowed by, in one value.
 *
 * They travel together because a cursor belongs to the query it was issued for
 * — change any of them and the feed restarts rather than pages on — and because
 * five of them as arguments was a signature nobody could read.
 *
 * Every one is the service's to apply. `order` is sent rather than used to
 * reverse a page we hold, which would only reverse that page and still start at
 * the newest event; `runState` becomes `state` and `outcome`, so the funnel's
 * counts cannot disagree with the list under them; and the window and the work
 * order narrow where the paging happens, not after it.
 */
interface AOPFeedQuery {
	order: AOPEventOrder;
	runState: AOPRunStateName | null;
	/** One work order out of the procedure's whole feed. */
	workOrderId: number | null;
	/** Inclusive bounds on `occurred_at`. The service takes date-times. */
	from: string | null;
	to: string | null;
}

/** The target collection every other list names. This one has no redux slice. */
const FILTER_TC = 'procedure_activity';

/**
 * The sort, in the shape the shared `Sorter` matches on.
 *
 * It compares `sort_by` and `order` together, and this feed is ordered by time
 * alone — so `sort_by` is a constant that never leaves the browser. Only
 * `order` reaches the service.
 */
const SORT_BY = 'occurred_at';

const SORTER_ITEMS = [
	{ selectVal: 'newest', label: 'Newest first', value: { sort_by: SORT_BY, order: 'desc' } },
	{ selectVal: 'oldest', label: 'Oldest first', value: { sort_by: SORT_BY, order: 'asc' } },
];

/**
 * The repo's range picker, asking for the time as well as the date.
 *
 * Wrapped here rather than passed through the filter config, because
 * `AdvancedFilters` hands its controls a fixed set of props and `showTime` is
 * not among them — widening that for one screen would touch every list.
 */
const AOPWhenPicker = (props: any) => <OWDateRangePicker {...props} showTime />;

const WHOLE_FEED: AOPFeedQuery = {
	order: 'desc',
	runState: null,
	workOrderId: null,
	from: null,
	to: null,
};

/**
 * The Activity tab: everything Radar has done on this procedure, across every
 * work order, newest first.
 *
 * The Work Orders tab answers "what is Radar on"; this answers "what has Radar
 * been doing". Same events, read from the procedure rather than from one run,
 * which is why every line has to name the work order it belongs to.
 *
 * An event says which run it came from but not what became of that run, so the
 * runs are read alongside it and joined by id to colour each line. A line whose
 * run has not been paged in yet shows no state rather than a guessed one.
 *
 * Nor does an event say where it sits in the plan, which is what the design
 * leads each line with. A run pins a published version, so the position has to
 * be read off that version's plan: the runs name their versions, and the ones
 * not already in the store are fetched — one request per distinct version on
 * the page, not per run.
 */

const AOPActivityTab: FC<any> = ({
	match,
	history,
	location,
	userType,
	workOrdersSlice,
	versions,
	loadEvents,
	loadExecution,
	loadExecutions,
	loadWorkOrders,
	loadVersion,
}) => {
	const procedureId = nullSafeGet('params.id', match);
	// Grey page, white table — the tab body is white for every other tab, so the
	// grey is this screen's own and has to reach the bottom of the window.
	const [fillRef, fillHeight] = useFillViewportHeight(480);

	const [events, setEvents] = useState<AOPProcedureEvent[]>([]);

	/*
	  The work order in the address bar, if there is one.
	
	  Read once, to open on it. It is in the URL because `AdvancedFilters` keeps
	  the search box's own state and decides whether to report a change by
	  comparing it with `?search=` — so with nothing there, emptying the box
	  compares undefined against undefined and the filter is never lifted. The
	  tab writes the parameter on every search for that reason, and gets a
	  shareable link and a filter that survives a reload out of it.
	*/
	const [query, setQuery] = useState<AOPFeedQuery>(() => {
		const asked = parseInt(
			String(
				queryString.parse(nullSafeGetOrElse('search', location, ''), {
					ignoreQueryPrefix: true,
				}).search
			),
			10
		);

		return { ...WHOLE_FEED, workOrderId: asked > 0 ? asked : null };
	});

	/** The query as it is now, for the two callbacks that fire in one tick. */
	const queryRef = useRef(query);
	const [runs, setRuns] = useState<{ [executionId: string]: AOPExecution }>({});
	const [cursor, setCursor] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(
		(after: string | null, asked: AOPFeedQuery) =>
			loadEvents(procedureId, after, PAGE_SIZE, asked).then((page) => {
				const rows = pageRows<AOPProcedureEvent>(page);

				setEvents((previous) => (after === null ? rows : previous.concat(rows)));
				setCursor(page.next_cursor);
				setError(null);

				/*
				  The plans these lines sit in, from the events themselves.

				  A line is led by the step's place in the plan its run pinned, and
				  the event names that plan — so this no longer waits for the run to
				  be read, and a line keeps its position even when that read fails.
				  A version is immutable once cut, so one fetch serves every event
				  that pinned it and never needs refreshing.
				*/
				Array.from(new Set(rows.map((event) => versionKey(event.procedure_id, event.version))))
					.filter((key) => !versions[key])
					.forEach((key) => {
						const event = rows.filter(
							(row) => versionKey(row.procedure_id, row.version) === key
						)[0];
						loadVersion(event.procedure_id, event.version).catch(() => undefined);
					});

				/*
				  The runs behind this page that the first hundred did not cover.

				  Read for what became of them — an event says which run it came from
				  but not how that run ended, and the state badge on each line is the
				  run's. The run list is read once, newest first, so a feed read
				  oldest first, or paged far enough into a newest-first one, reaches
				  events whose run was never in it. In the ordinary case there are
				  none.
				*/
				Array.from(new Set(rows.map((event) => event.execution_id)))
					.filter((executionId) => !runs[executionId])
					.forEach((executionId) =>
						loadExecution(executionId)
							.then((execution: AOPExecution) =>
								setRuns((previous) => ({ ...previous, [execution.id]: execution }))
							)
							.catch(() => undefined)
					);

				const ids = Array.from(new Set(rows.map((event) => event.work_order_id)));
				if (ids.length > 0) {
					loadWorkOrders(ids).catch(() => undefined);
				}
			}),
		// `runs` and `versions` are read to decide what is missing, so a page that
		// fills them must rebuild this — otherwise the next page asks for runs it
		// already has.
		[loadEvents, loadExecution, loadVersion, loadWorkOrders, procedureId, runs, versions]
	);

	/**
	 * Both controls restart the feed. A cursor belongs to the query it was
	 * issued for — the order it was read in and the runs it was narrowed to — so
	 * it cannot be carried across a change to either.
	 */
	const restart = useCallback(
		(patch: Partial<AOPFeedQuery>, force = false) => {
			/*
			  Composed through a ref, not through `query`.
			
			  Clearing calls back twice in one tick — once to drop the filters and
			  once to put the sort back — and both would otherwise merge onto the
			  same render's `query`, so the second would undo the first and the
			  filters would come straight back.
			*/
			const asked = { ...queryRef.current, ...patch };

			/*
			  Nothing actually moved: the second of those two calls, or the same
			  sort picked again.

			  `force` is the one case where asking the same question again is the
			  point. Retrying a failed read patches nothing, so it is equal by
			  definition — guarded, the button did nothing at all and the only way
			  out of an error was to change a filter or reload the page.
			*/
			if (!force && isEqual(asked, queryRef.current)) {
				return;
			}

			queryRef.current = asked;
			setQuery(asked);
			setEvents([]);
			setCursor(null);
			setIsLoading(true);
			load(null, asked)
				.then(() => setIsLoading(false))
				.catch((error) => {
					// A read the next restart replaced: the screen belongs to that
					// query now, so neither the error nor the spinner is ours to set.
					if (isCancelledRead(error)) {
						return;
					}

					setError('Could not load what Radar has been doing.');
					setIsLoading(false);
				});
		},
		[load]
	);

	useMountEffect(() => {
		/*
		  The query as it was seeded, not the whole feed.

		  `?search=` puts a work order in the query before the first read, so
		  passing `WHOLE_FEED` here landed with the box filled in and the feed
		  unfiltered — the one state the URL exists to prevent.
		*/
		load(null, query)
			.then(() => setIsLoading(false))
			.catch((error) => {
				if (isCancelledRead(error)) {
					return;
				}

				setError('Could not load what Radar has been doing.');
				setIsLoading(false);
			});

		// The runs, for the state beside each line. Merged rather than replaced so
		// a later page cannot forget what an earlier one established.
		loadExecutions(procedureId)
			.then((page) => {
				const next: { [executionId: string]: AOPExecution } = {};
				pageRows<AOPExecution>(page).forEach((execution) => {
					next[execution.id] = execution;
				});
				setRuns((previous) => ({ ...previous, ...next }));

				// No plans fetched here. This list is read newest-first and may hold
				// runs whose events are nowhere on the page, so fetching their plans
				// bought documents no line was going to use; the events ask for the
				// ones their own lines need.
			})
			.catch(() => undefined);
	});

	const onLoadMore = useCallback(() => {
		setIsLoadingMore(true);
		load(cursor, query)
			.then(() => setIsLoadingMore(false))
			.catch((error) => {
				/*
				  Cleared whichever way this ends, unlike the restarts above.

				  A restart takes over `isLoading` and will clear it itself, so a
				  cancelled read leaves that one alone. Nothing takes over this flag
				  — the new feed does not page — so returning early here left the
				  button spinning for good.
				*/
				setIsLoadingMore(false);

				if (isCancelledRead(error)) {
					return;
				}

				setError('Could not load more activity.');
			});
	}, [cursor, load, query]);

	/*
	  The feed's own query standing in for the redux slice every other list
	  hands this component.

	  `AdvancedFilters` reads `filtersStateSlice` directly when it holds no key
	  matching the target collection, which is the door in: this tab keeps its
	  query in state, and still gets the same search box, Filters popover, sort
	  and Clear as every other list in the app.
	*/
	const filtersStateSlice = useMemo(() => {
		/*
		  Only what is set. Clear decides it has something to clear by looking for
		  keys rather than values, and anything it cannot match against a default
		  counts — so a `runState: null` sitting here permanently kept the button
		  on screen from the moment the tab opened.
		*/
		const active: any = {};

		if (query.workOrderId !== null) {
			active.search = String(query.workOrderId);
		}

		if (query.runState !== null) {
			active.runState = query.runState;
		}

		if (query.from || query.to) {
			active.occurredAt = [
				query.from ? moment(query.from) : null,
				query.to ? moment(query.to) : null,
			];
		}

		return active;
	}, [query]);

	/**
	 * One patch from the strip, in the service's terms.
	 *
	 * Keyed on what is present rather than read wholesale, because the search
	 * box and each filter report only their own field — reading the rest would
	 * clear them.
	 */
	const onUpdateFilters = useCallback(
		(patch: any) => {
			const next: Partial<AOPFeedQuery> = {};

			if ('search' in patch) {
				// The box searches one thing, which is why it says so: `work_order_id`
				// is the filter the service offers, and a half-typed number is simply
				// a different work order rather than a partial match.
				const typed = (patch.search ? patch.search : '').trim();
				const workOrderId = parseInt(typed, 10);
				next.workOrderId = typed.length > 0 && workOrderId > 0 ? workOrderId : null;

				/*
				  Into the address bar, which is what lets the box be emptied: the
				  search control reports a change only when its text differs from
				  `?search=`, so without this the parameter stays absent and clearing
				  the box compares undefined with undefined and reports nothing.

				  `replace`, not `push` — a search is a view of this tab rather than a
				  place, and Back should leave the tab rather than walk its filters.
				*/
				history.replace({
					pathname: nullSafeGet('pathname', location),
					search: next.workOrderId === null ? '' : `?search=${next.workOrderId}`,
				});
			}

			if ('runState' in patch) {
				next.runState = patch.runState ? patch.runState : null;
			}

			if ('occurredAt' in patch) {
				// Whatever was picked, to the minute. Both bounds are optional — the
				// picker allows one empty — so each is read on its own.
				const range = patch.occurredAt;
				next.from = range && range[0] ? range[0].toISOString() : null;
				next.to = range && range[1] ? range[1].toISOString() : null;
			}

			restart(next);
		},
		[history, location, restart]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Status',
				fieldName: 'runState',
				component: OWStaticSelect,
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				placeholder: 'Any status',
				items: AOP_RUN_ORDER.map((runState) => ({
					label: AOP_RUN_STATES[runState].label,
					value: runState,
				})),
				keyAccessor: (item: any) => item.value,
				valueAccessor: (item: any) => item.value,
				renderRecord: (item: any) => item.label,
				filtersValueAccessor: (slice: any) => slice.runState,
				handleChange: (runState: any) => onUpdateFilters({ runState }),
			},
			{
				label: 'When',
				fieldName: 'occurredAt',
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				labelPrefix: 'Between',
				component: AOPWhenPicker,
				valueAccessor: (value: any) => value,
				renderItem: (value: any) => value,
				filtersValueAccessor: (slice: any) => slice.occurredAt,
				handleChange: (range: any) => onUpdateFilters({ occurredAt: range }),
			},
		],
		[onUpdateFilters]
	);

	/*
	  Rendered in every branch, loading and empty included: narrowing to
	  something with nothing in it must not take away the control that would
	  widen it again.
	*/
	const controls = (
		<AdvancedFilters
			filtersTargetCollectionName={FILTER_TC}
			filtersStateSlice={filtersStateSlice}
			filterConfig={filterConfig}
			updateFilters={onUpdateFilters}
			clearAndUpdateFilters={() =>
				// The sort is not a filter, so Clear leaves it where the author put it.
				restart({ workOrderId: null, runState: null, from: null, to: null })
			}
			applySorting={(next: any) => restart({ order: next.order })}
			sorters={{ sort_by: SORT_BY, order: query.order }}
			sorterItems={SORTER_ITEMS}
			/*
			  Without this, Clear never goes away: it decides whether anything is
			  active by excluding the default sort, and with none given it compares
			  against the app's own `createdAt` — which this feed's `occurred_at`
			  never matches, so the sort always counted as a filter.
			*/
			defaultSorters={{ sort_by: SORT_BY, order: WHOLE_FEED.order }}
			filterPlaceHolder="Search by work order #"
		/>
	);

	// Whether anything is narrowing the feed, which decides what an empty page
	// is allowed to claim. The order is not narrowing — it cannot empty a page.
	const isNarrowed =
		query.runState !== null ||
		query.workOrderId !== null ||
		// Either bound. The picker allows one empty, so a window with only an end
		// to it narrows the feed exactly as much as one with both.
		query.from !== null ||
		query.to !== null;

	const rows = useMemo(
		() =>
			events.filter(isListedEvent).map((event) => {
				const run = runs[event.execution_id];
				const workOrder = getEntityById(event.work_order_id, workOrdersSlice, null);
				// Bracket access: the store key is a composite identifier, not a path.
				// Keyed from the event, so a line that has no run yet still has a plan.
				const pinned = versions[versionKey(event.procedure_id, event.version)];
				/*
				  The site's zone, where the work order names one.

				  Not `getWorkOrderTimeZoneId`: that ends its fallback chain at
				  `defaultTimeZone`, which is New York — so a work order without a
				  location would put a London reader on Eastern time without saying
				  so. Undefined instead, which is what `DateTimeHover` takes as "use
				  the viewer's".
				*/
				const timeZoneId = workOrder ? nullSafeGet('location.timeZoneId', workOrder) : undefined;

				return {
					event,
					/*
					  Described in the same zone the row's timestamp is shown in. A
					  `waiting` event's "Next check" was formatted in the viewer's zone
					  while the Time cell beside it read the site's, so one row carried
					  two unlabelled clocks for anybody not sitting at the site.
					*/
					described: describeEvent(event, timeZoneId),
					position: positionOf(event, pinned ? pinned.plan : null),
					chrome: run ? AOP_RUN_STATES[runStateOf(run)] : null,
					title: workOrder ? nullSafeGet('title', workOrder) : null,
					timeZoneId,
				};
			}),
		[events, runs, versions, workOrdersSlice]
	);

	const columns = [
		{
			title: 'Time',
			dataIndex: 'event',
			width: 180,
			render: (_, row) => (
				/*
				  Two `DateTimeHover`s stacked, the way the work order's own Radar
				  timeline draws the same thing — rather than formatting moment by
				  hand here, which is how this cell came to carry milliseconds and no
				  zone while every other Radar surface showed both.

				  `showDate` is what makes it print the format given instead of
				  "3 minutes ago" in a popover.

				  To the second and no further: two events of one chase land close
				  together — an attempt and the success after it — so the seconds
				  tell them apart, while the milliseconds only made every row wider
				  and read like a log rather than a history.
				*/
				<div className="flex flex-col">
					<span className="bodyregular text-character-primary">
						<DateTimeHover
							timestamp={row.event.occurred_at}
							timeZone={row.timeZoneId}
							showDate
							dateFormat="h:mm:ss a z"
						/>
					</span>
					<span className="footnoteregular text-character-secondary">
						<DateTimeHover
							timestamp={row.event.occurred_at}
							timeZone={row.timeZoneId}
							showDate
							dateFormat="D MMM, YYYY"
						/>
					</span>
				</div>
			),
		},
		{
			title: 'Work order',
			dataIndex: 'title',
			render: (_, row) => (
				<div className="flex flex-col gap-2xs">
					<div className="flex items-center gap-xs">
						<Link to={`/${userType}/workOrders/detail/${row.event.work_order_id}/details`}>
							#WO {row.event.work_order_id}
						</Link>
						{row.chrome ? (
							<span className={`flex items-center gap-2xs ${row.chrome.textClass}`}>
								<OscarMark size={16} animation={row.chrome.isAnimated ? 'flap' : undefined} />
								<span className="bodyregular">{row.chrome.label}</span>
							</span>
						) : null}
					</div>
					{row.title ? (
						<span className="footnoteregular text-character-secondary">{row.title}</span>
					) : null}
				</div>
			),
		},
		{
			title: 'Radar activity',
			dataIndex: 'described',
			render: (_, row) => (
				<div className="flex flex-col gap-2xs">
					<span className="bodyregular text-character-primary">
						{/*
						  Where the run is, in front of what it did. Absent until the
						  pinned version has loaded.
						*/}
						{row.position ? (
							<span className="mr-xs text-character-secondary">{row.position}</span>
						) : null}
						<AOPEventSentence described={row.described} />
					</span>
					{row.described.supplier || row.described.detail ? (
						<span className="footnoteregular flex flex-wrap items-center gap-2xs text-character-secondary">
							{row.described.supplier ? (
								<span>
									<Link
										to={`/${userType}/suppliers/detail/${row.described.supplier.id}`}
										target="_blank"
									>
										{row.described.supplier.name}
									</Link>{' '}
									assigned.
								</span>
							) : null}
							{row.described.detail ? <span>{row.described.detail}</span> : null}
						</span>
					) : null}
				</div>
			),
		},
	];

	/*
	  The table's own loading and empty states, rather than two early returns
	  around it.

	  Those returns put the control strip at a different place in the tree
	  depending on what had loaded, so flipping `isLoading` — which every control
	  does — unmounted it and took its own state with it: the search box emptied
	  itself halfway through a word. Nothing above the rows re-mounts now.
	*/
	/*
	  One empty state, whatever emptied it.

	  A failed read used to put an alert panel in the table's place instead —
	  boxed, pink, and laid out nothing like the empty state beside it, which
	  made a feed that could not be read look like a broken page rather than a
	  list with nothing in it. It is the same centred block either way; only the
	  words change, and a failure keeps the retry inside them.

	  The two still say different things. "Radar has not done anything yet" is a
	  claim about the procedure, and a read that failed is exactly what cannot
	  establish it — so that sentence is only reached when something did load and
	  there was nothing in it.
	*/
	const emptyText = (
		<EmptyState
			graphic={
				error ? null : (
					<img
						alt=""
						style={{ marginBottom: 8 }}
						src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
					/>
				)
			}
			headline={
				error
					? 'Could not load what Radar has been doing'
					: !isNarrowed
					? 'Radar has not done anything yet'
					: query.runState !== null
					? `Nothing from ${AOP_RUN_STATES[query.runState].label.toLowerCase()} runs`
					: 'Nothing matches'
			}
			body={
				<div style={{ textAlign: 'center' }}>
					<div style={{ maxWidth: 440, marginBottom: 16 }}>
						{error
							? 'This does not mean Radar has done nothing — the feed could not be read.'
							: !isNarrowed
							? 'A line lands here for every step Radar takes on a work order.'
							: query.runState !== null
							? 'Radar has done things on this procedure, but none on a run in this state.'
							: 'Radar has done things on this procedure, but none inside what you have asked for.'}
					</div>
					{error ? (
						<Button loading={isLoading} onClick={() => restart({}, true)}>
							Try again
						</Button>
					) : null}
				</div>
			}
			height={225}
		/>
	);

	return (
		<div
			ref={fillRef}
			className="-mt-m flex flex-col gap-m bg-grey-3 px-xs py-m"
			style={{ minHeight: fillHeight }}
		>
			{/* Beside rows that did load. With none, the failure fills the table instead. */}
			{error && events.length > 0 ? <Alert type="error" showIcon message={error} /> : null}

			{controls}

			{/*
			  The feed is the only white surface on the screen, and it is the app's
			  own table so the header, borders and row rhythm are the ones every
			  other list uses. Paging is the service's cursor, not a page number,
			  so the table draws none and "Load more" sits under it.
			*/}
			<Table
				columns={columns}
				dataSource={rows}
				rowKey={(row: any) => `${row.event.execution_id}:${row.event.sequence}`}
				pagination={false}
				loading={isLoading}
				locale={{ emptyText }}
			/>

			{/*
			  Full width and dashed, directly under the last row.

			  A button the width of its own text, floating in the grey below the
			  table, read as a thing beside the feed rather than the bottom of it —
			  and the one place a reader looks for more is the end of what they have
			  been reading. Dashed because it is an edge rather than a surface: it
			  says the list continues, where a solid bar would look like another
			  row.
			*/}
			{cursor === null ? null : (
				<Button
					block
					size="large"
					loading={isLoadingMore}
					onClick={onLoadMore}
					className="aop-sentence-case h-auto border border-dashed border-grey-5 bg-grey-1 py-s text-character-secondary hover:border-purple-5 hover:text-purple-6"
				>
					{isLoadingMore ? 'Loading…' : 'Load more'}
				</Button>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	workOrdersSlice: state.work_orders,
	versions: nullSafeGetOrElse('agent_procedure_versions.versions', state, {}),
});

const mapDispatchToProps = (dispatch) => ({
	loadEvents: (procedureId: string, after: string | null, limit: number, asked: AOPFeedQuery) =>
		dispatch(
			readProcedureEvents(
				procedureId,
				{
					after,
					limit,
					order: asked.order,
					...(asked.runState ? filtersForRunState(asked.runState) : {}),
					// Dropped from the query string when null, so an unset control
					// narrows nothing rather than asking for nothing.
					work_order_id: asked.workOrderId,
					occurred_at_start_date: asked.from,
					occurred_at_end_date: asked.to,
				},
				/*
				  One in-flight read for this feed. Every control restarts it, and
				  without this a slow answer to the query just left behind could land
				  last and sit under controls that no longer asked for it.
				*/
				`procedure_events:${procedureId}`
			)
		),
	// One run, by id — for an event whose run the newest-hundred read missed.
	loadExecution: (executionId: string) => dispatch(readExecution(executionId)),
	loadExecutions: (procedureId: string) =>
		dispatch(readProcedureExecutions(procedureId, { limit: 100 })),
	loadVersion: (procedureId: string, version: number) =>
		dispatch(fetchProcedureVersion(procedureId, version)),
	loadWorkOrders: (workOrderIds: number[]) =>
		dispatch(
			workOrdersRestCrudThunksForBuyer.searchWithPostMethod(
				{ ids: workOrderIds.join(',') },
				WORK_ORDER_TC,
				{ current: 1, pageSize: workOrderIds.length },
				null,
				null,
				false,
				workOrderSearchAndAPIAdaptor
			)
		),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(AOPActivityTab))
);
